import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Cpu, FolderOpen, FolderPlus, Network, Pencil, Plus, Radio, Server, Video, Trash2, Zap } from 'lucide-react';
import api from '../../services/api';
import DispositivoModal from './DispositivoModal';
import GrupoModal from './GrupoModal';
import Esp32Modal from './Esp32Modal';
import RtspModal from './RtspModal';
import FusaoModal from './FusaoModal';
import FusaoDevices from './FusaoDevices';

const DispositivoPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGrupoModalOpen, setIsGrupoModalOpen] = useState(false);
  const [dispositivos, setDispositivos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [dispositivoSelecionado, setDispositivoSelecionado] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [grupoSelecionadoId, setGrupoSelecionadoId] = useState(null);
  const [grupoParaEditar, setGrupoParaEditar] = useState(null);
  const [esp32, setEsp32] = useState([]);
  const [esp32ModalOpen, setEsp32ModalOpen] = useState(false);
  const [esp32Selecionada, setEsp32Selecionada] = useState(null);
  const [esp32Status, setEsp32Status] = useState({});
  const [releEmAcao, setReleEmAcao] = useState(null);
  const [historico, setHistorico] = useState({});
  const [camerasRtsp, setCamerasRtsp] = useState([]);
  const [rtspModalOpen, setRtspModalOpen] = useState(false);
  const [cameraRtspSelecionada, setCameraRtspSelecionada] = useState(null);
  const [fusaoOpen, setFusaoOpen] = useState(false);
  const [fusoes, setFusoes] = useState([]);
  const [fusaoSelecionada, setFusaoSelecionada] = useState(null);

  const fetchDispositivos = async () => {
    try {
      const response = await api.get('/dispositivos/');
      setDispositivos(response.data);
    } catch (error) {
      console.error('Erro ao buscar dispositivos:', error);
    }
  };

  const fetchFusoes = async () => {
    try {
      const response = await api.get('/dispositivos/fusoes');
      setFusoes(response.data);
    } catch (error) {
      // O interceptor trata 401 encerrando a sessão; não deixe a tela quebrar durante a transição.
      console.error('Erro ao buscar fusões:', error);
      setFusoes([]);
    }
  };

  const fetchCamerasRtsp = async () => { try { const response = await api.get('/dispositivos/cameras-rtsp'); setCamerasRtsp(response.data); } catch (error) { console.error('Erro ao buscar câmeras RTSP:', error); } };

  const fetchEsp32 = async () => {
    try { const response = await api.get('/dispositivos/esp32'); setEsp32(response.data); }
    catch (error) { console.error('Erro ao buscar ESP32:', error); }
  };

  const fetchGrupos = async () => {
    try {
      const response = await api.get('/dispositivos/grupos');
      setGrupos(response.data);
    } catch (error) {
      console.error('Erro ao buscar grupos:', error);
    }
  };

  useEffect(() => {
    fetchDispositivos();
    fetchGrupos();
    fetchEsp32();
    fetchCamerasRtsp();
    fetchFusoes();
  }, []);

  const handleNovoDispositivo = () => {
    setDispositivoSelecionado(null);
    setIsModalOpen(true);
  };

  const handleConfigurarDispositivo = (dispositivo) => {
    const fusao = fusoes.find((item) => item.dispositivo_id === dispositivo.id);
    if (fusao) { setFusaoSelecionada(fusao); setFusaoOpen(true); return; }
    setDispositivoSelecionado(dispositivo);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setDispositivoSelecionado(null);
  };

  const handleCloseGrupoModal = () => {
    setIsGrupoModalOpen(false);
    setGrupoParaEditar(null);
  };

  const handleRefreshAll = async () => {
    await Promise.all([fetchDispositivos(), fetchGrupos(), fetchEsp32(), fetchCamerasRtsp(), fetchFusoes()]);
  };

  const handleTestarConexao = async (dispositivo) => {
    setTestingId(dispositivo.id);
    try {
      const response = await api.post(`/dispositivos/${dispositivo.id}/testar-conexao`);
      setDispositivos((current) => current.map((item) => item.id === dispositivo.id ? { ...item, status_operacao: response.data?.status_operacao || 'Conexão OK' } : item));
      alert(`${dispositivo.nome} (${dispositivo.marca || 'sem marca'}): ${response.data?.message || 'Conexão OK'}`);
    } catch (error) {
      console.error(error);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.error;
      alert(`${dispositivo.nome} (${dispositivo.marca || 'sem marca'}): ${message || 'Erro ao testar conexão'}`);
      await fetchDispositivos();
    } finally {
      setTestingId(null);
    }
  };

  const handleExcluirDispositivo = async (dispositivo) => {
    const confirmou = window.confirm(`Excluir a facial "${dispositivo.nome}"?`);
    if (!confirmou) {
      return;
    }

    try {
      await api.delete(`/dispositivos/${dispositivo.id}`);
      await handleRefreshAll();
      alert('Facial excluída com sucesso!');
    } catch (error) {
      console.error(error);
      const detail = error.response?.data?.detail;
      alert(detail || 'Erro ao excluir facial');
    }
  };

  const handleExcluirGrupo = async (grupo) => {
    const confirmou = window.confirm(
      `Excluir o grupo "${grupo.nome}"? As faciais continuarão cadastradas e ficarão sem grupo.`
    );
    if (!confirmou) {
      return;
    }

    try {
      await api.delete(`/dispositivos/grupos/${grupo.id}`);
      if (grupoSelecionadoId === grupo.id) {
        setGrupoSelecionadoId(null);
      }
      await handleRefreshAll();
      alert('Grupo excluído com sucesso!');
    } catch (error) {
      console.error(error);
      const detail = error.response?.data?.detail;
      alert(detail || 'Erro ao excluir grupo');
    }
  };

  const getEsp32Error = (error) => {
    const detail = error.response?.data?.detail;
    return typeof detail === 'string' ? detail : detail?.message || 'Não foi possível concluir a operação.';
  };

  const consultarStatusEsp32 = async (controladora) => {
    setEsp32Status((current) => ({ ...current, [controladora.id]: { loading: true } }));
    try { const response = await api.get(`/dispositivos/esp32/${controladora.id}/status`); setEsp32Status((current) => ({ ...current, [controladora.id]: response.data })); }
    catch (error) { setEsp32Status((current) => ({ ...current, [controladora.id]: { online: false, message: getEsp32Error(error) } })); }
  };
  const carregarHistoricoEsp32 = async (controladora) => {
    try { const response = await api.get(`/dispositivos/esp32/${controladora.id}/acionamentos`); setHistorico((current) => ({ ...current, [controladora.id]: response.data })); }
    catch (error) { alert(getEsp32Error(error)); }
  };
  const acionarReleEsp32 = async (controladora, rele, teste = false) => {
    const key = `${controladora.id}-${rele.numero}-${teste ? 'teste' : 'acionar'}`;
    setReleEmAcao(key);
    try { const response = await api.post(`/dispositivos/esp32/${controladora.id}/reles/${rele.numero}/acionar`, null, { params: { teste } }); alert(`${rele.nome}: ${response.data.message}`); await carregarHistoricoEsp32(controladora); }
    catch (error) { alert(`${rele.nome}: ${getEsp32Error(error)}`); } finally { setReleEmAcao(null); }
  };
  const excluirEsp32 = async (controladora) => {
    if (!window.confirm(`Excluir a controladora "${controladora.nome}"?`)) return;
    try { await api.delete(`/dispositivos/esp32/${controladora.id}`); await fetchEsp32(); } catch (error) { alert(getEsp32Error(error)); }
  };

  const excluirCameraRtsp = async (camera) => { if (!window.confirm(`Excluir a câmera "${camera.nome}"?`)) return; try { await api.delete(`/dispositivos/cameras-rtsp/${camera.id}`); await fetchCamerasRtsp(); } catch (error) { alert(getEsp32Error(error)); } };

  const handleNovoGrupo = () => {
    setGrupoParaEditar(null);
    setIsGrupoModalOpen(true);
  };

  const handleEditarGrupo = (grupo) => {
    setGrupoParaEditar(grupo);
    setIsGrupoModalOpen(true);
  };

  const dispositivosSemGrupo = useMemo(
    () => dispositivos.filter((dispositivo) => !dispositivo.grupo_ids?.length),
    [dispositivos]
  );
  const faciaisInstaladas = dispositivos.filter((device) => device.marca !== 'rtsp_lpr');

  const grupoSelecionado = useMemo(
    () => grupos.find((grupo) => grupo.id === grupoSelecionadoId) || null,
    [grupos, grupoSelecionadoId]
  );

  const dispositivosDoGrupoSelecionado = useMemo(() => {
    if (!grupoSelecionadoId) {
      return [];
    }
    return dispositivos.filter((dispositivo) => dispositivo.grupo_ids?.includes(grupoSelecionadoId));
  }, [dispositivos, grupoSelecionadoId]);

  return (
    <div className="flex min-h-full flex-col gap-5 px-1 sm:px-2 lg:px-4 xl:px-6 animate-in fade-in duration-500">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 sm:text-3xl">Dispositivos</h1>
          <p className="mt-1 text-sm text-gray-500">Gerencie faciais, leitores de placas e os equipamentos de cada acesso.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {false && <button
            onClick={handleNovoGrupo}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition duration-200 hover:border-slate-300 hover:bg-slate-50 hover:shadow"
          >
            <FolderPlus size={18} /> Criar Grupo
          </button>}
          <button onClick={() => { setFusaoSelecionada(null); setFusaoOpen(true); }} className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white">Adicionar Fusão</button>
          <button onClick={() => { setCameraRtspSelecionada(null); setRtspModalOpen(true); }} className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-800"><Video size={18}/>Adicionar RTSP</button>
          <button
            onClick={() => { setEsp32Selecionada(null); setEsp32ModalOpen(true); }}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:bg-sky-800 hover:shadow-md"
          >
            <Radio size={18} /> Adicionar ESP32
          </button>
          <button
            onClick={handleNovoDispositivo}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:bg-sky-800 hover:shadow-md"
          >
            <Plus size={18} /> Adicionar Facial
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 xl:grid-cols-3">
        <StatCard icon={<Cpu size={18} />} color="blue" label="Total de Faciais" value={faciaisInstaladas.length} />
        <StatCard icon={<FolderOpen size={18} />} color="amber" label="Grupos Criados" value={grupos.length} />
        <StatCard icon={<Network size={18} />} color="emerald" label="Sem Grupo" value={dispositivosSemGrupo.length} />
      </div>

      {!grupoSelecionado && (
        <div className="space-y-5">
          <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Faciais instaladas</h2>
                <p className="mt-1 text-sm text-gray-500">Lista de todas as faciais cadastradas no sistema.</p>
              </div>
              <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                {faciaisInstaladas.length} facial(is)
              </span>
            </div>
            <TabelaDispositivos
              dispositivos={faciaisInstaladas}
              testingId={testingId}
              onTestarConexao={handleTestarConexao}
              onConfigurar={handleConfigurarDispositivo}
              onExcluir={handleExcluirDispositivo}
              emptyMessage="Nenhuma facial instalada."
              showGroups={false}
            />
          </section>
        </div>
      )}

      <FusaoDevices devices={fusoes} cameras={camerasRtsp} controllers={esp32} groups={grupos} onConfigure={(device) => { setFusaoSelecionada(device); setFusaoOpen(true); }} onRefresh={handleRefreshAll} />

      <section className="rounded-2xl border border-violet-100 bg-white p-4 shadow-sm sm:p-5"><div className="mb-4 flex items-center justify-between"><div><h2 className="text-lg font-bold text-slate-800">Câmeras RTSP</h2><p className="mt-1 text-sm text-slate-500">Câmeras cadastradas para integração de monitoramento.</p></div><button onClick={() => { setCameraRtspSelecionada(null); setRtspModalOpen(true); }} className="rounded-xl bg-violet-700 px-4 py-2 text-sm font-semibold text-white"><Plus size={16} className="inline"/> Adicionar RTSP</button></div>{camerasRtsp.length === 0 ? <p className="rounded-xl border border-dashed p-6 text-center text-sm text-slate-500">Nenhuma câmera RTSP cadastrada.</p> : <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{camerasRtsp.map((camera) => <article key={camera.id} className="rounded-xl border p-4"><div className="flex items-start justify-between gap-2"><div><p className="font-bold text-slate-800">{camera.nome}</p><p className="text-sm text-slate-500">{camera.localizacao || 'Sem localização'}</p></div><Video className="text-violet-700" size={20}/></div><p className="mt-3 truncate font-mono text-xs text-slate-500">{camera.url_rtsp}</p><div className="mt-3 flex gap-2"><button onClick={() => { setCameraRtspSelecionada(camera); setRtspModalOpen(true); }} className="text-xs font-semibold text-blue-700">Configurar</button><button onClick={() => excluirCameraRtsp(camera)} className="text-xs font-semibold text-red-600">Excluir</button></div></article>)}</div>}</section>
      <RtspModal isOpen={rtspModalOpen} onClose={() => { setRtspModalOpen(false); setCameraRtspSelecionada(null); }} onSaved={fetchCamerasRtsp} camera={cameraRtspSelecionada} />

      <section className="rounded-2xl border border-sky-100 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><div><h2 className="text-lg font-bold text-slate-800">Controladoras ESP32</h2><p className="mt-1 text-sm text-gray-500">Relés para portões e fechaduras. Os comandos são encaminhados pelo backend.</p></div><button onClick={() => { setEsp32Selecionada(null); setEsp32ModalOpen(true); }} className="inline-flex items-center justify-center gap-2 rounded-xl bg-sky-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-800"><Plus size={18}/>Adicionar ESP32</button></div>
        {esp32.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 px-6 py-8 text-center text-sm text-slate-500">Nenhuma controladora ESP32 cadastrada.</div> : <div className="space-y-4">{esp32.map((controladora) => { const status = esp32Status[controladora.id]; const logs = historico[controladora.id]; return <article key={controladora.id} className="rounded-xl border border-slate-200 p-4"><div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"><div><div className="flex items-center gap-2"><Radio size={19} className="text-sky-700"/><h3 className="font-bold text-slate-800">{controladora.nome}</h3>{status && !status.loading && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${status.online ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{status.online ? 'Online' : 'Offline'}</span>}</div><p className="mt-1 font-mono text-xs text-slate-500">{controladora.ip}:{controladora.porta}</p></div><div className="flex flex-wrap gap-2"><button onClick={() => consultarStatusEsp32(controladora)} className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">{status?.loading ? 'Consultando...' : 'Status'}</button><button onClick={() => { setEsp32Selecionada(controladora); setEsp32ModalOpen(true); }} className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">Configurar</button><button onClick={() => excluirEsp32(controladora)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={16}/></button></div></div>{status?.message && <p className="mt-2 text-xs text-slate-500">{status.message}</p>}<div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">{controladora.reles.map((rele) => { const testKey = `${controladora.id}-${rele.numero}-teste`; const actionKey = `${controladora.id}-${rele.numero}-acionar`; return <div key={rele.id} className="rounded-xl bg-slate-50 p-3"><p className="font-semibold text-slate-800">{rele.nome}</p><p className="mb-3 text-xs text-slate-500">Relé {rele.numero} · {rele.duracao_ms} ms</p><div className="flex gap-2"><button disabled={Boolean(releEmAcao)} onClick={() => acionarReleEsp32(controladora, rele, true)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50">{releEmAcao === testKey ? 'Testando...' : 'Testar'}</button><button disabled={Boolean(releEmAcao)} onClick={() => acionarReleEsp32(controladora, rele)} className="inline-flex items-center gap-1 rounded-lg bg-sky-700 px-2 py-1.5 text-xs font-semibold text-white hover:bg-sky-800 disabled:opacity-50"><Zap size={13}/>{releEmAcao === actionKey ? 'Acionando...' : 'Acionar'}</button></div></div>; })}</div><button onClick={() => carregarHistoricoEsp32(controladora)} className="mt-4 text-xs font-semibold text-sky-700 hover:underline">Ver histórico de acionamentos</button>{logs && <div className="mt-2 max-h-44 overflow-auto rounded-lg border text-xs"><table className="w-full text-left"><thead className="bg-slate-50 text-slate-500"><tr><th className="p-2">Data/hora</th><th>Relé</th><th>Usuário</th><th>Tipo</th><th>Resultado</th></tr></thead><tbody>{logs.map((log) => <tr key={log.id} className="border-t"><td className="p-2">{new Date(log.criado_em + 'Z').toLocaleString('pt-BR')}</td><td>{log.rele_nome}</td><td>{log.usuario_nome || '—'}</td><td>{log.tipo}</td><td className={log.sucesso ? 'text-emerald-700' : 'text-red-700'}>{log.sucesso ? 'Sucesso' : 'Falhou'}</td></tr>)}{logs.length === 0 && <tr><td className="p-3 text-slate-500" colSpan="5">Nenhum acionamento registrado.</td></tr>}</tbody></table></div>}</article>; })}</div>}
      </section>
      <Esp32Modal isOpen={esp32ModalOpen} onClose={() => { setEsp32ModalOpen(false); setEsp32Selecionada(null); }} onSaved={fetchEsp32} controladora={esp32Selecionada} />

      <FusaoModal open={fusaoOpen} onClose={() => { setFusaoOpen(false); setFusaoSelecionada(null); }} onSaved={handleRefreshAll} fusao={fusaoSelecionada} />
      <DispositivoModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        refreshData={handleRefreshAll}
        dispositivoParaEditar={dispositivoSelecionado}
        grupos={grupos}
      />
      <GrupoModal
        isOpen={isGrupoModalOpen}
        onClose={handleCloseGrupoModal}
        refreshData={fetchGrupos}
        grupoParaEditar={grupoParaEditar}
      />
    </div>
  );
};

const StatCard = ({ icon, color, label, value }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    amber: 'bg-amber-50 text-amber-600',
    emerald: 'bg-emerald-50 text-emerald-600'
  };

  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition duration-200 hover:border-slate-200 hover:shadow">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${colors[color]}`}>{icon}</div>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
        <p className="text-xl font-bold text-slate-800">{value}</p>
      </div>
    </div>
  );
};


const MODELO_LABELS = {
  automatico: 'Automático',
  ss1530_1540: 'SS 1530 / SS 1540',
  ss3531: 'SS 3531',
};

const getModeloLabel = (modelo) => MODELO_LABELS[String(modelo || '').toLowerCase()] || null;

const TabelaDispositivos = ({ dispositivos, testingId, onTestarConexao, onConfigurar, onExcluir, emptyMessage, showGroups }) => {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full min-w-[1040px] text-left">
        <thead className="border-b border-gray-100 bg-slate-50">
          <tr>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Nome</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Marca / Modelo</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Status</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Controle de Acesso</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Abertura</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Endereço IP</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Usuário</th>
            {showGroups && <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Grupos</th>}
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Ações</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {dispositivos.map((disp) => (
            <tr key={disp.id} className="transition-colors duration-200 hover:bg-slate-50/80">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <Server size={16} />
                  </span>
                  <button
                    type="button"
                    onClick={() => onConfigurar(disp)}
                    className="text-left font-semibold text-slate-700 transition duration-200 hover:text-blue-600 hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-condo-teal"
                    title="Abrir configurações da facial"
                  >
                    {disp.nome}
                  </button>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col items-start gap-1">
                  <MarcaBadge marca={disp.marca} />
                  {String(disp.marca || '').toLowerCase() === 'intelbras' && getModeloLabel(disp.modelo) && (
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      {getModeloLabel(disp.modelo)}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <span role="status" className={`rounded-full px-2.5 py-1 text-xs font-semibold ${disp.status_operacao === 'Conexão OK' ? 'bg-emerald-50 text-emerald-700' : disp.status_operacao === 'Falha na conexão' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                  {disp.status_operacao || 'Não consultado'}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${disp.usa_controle_acesso !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {disp.usa_controle_acesso !== false ? 'Sim' : 'Não'}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${disp.usa_abertura !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {disp.usa_abertura !== false ? 'Sim' : 'Não'}
                </span>
              </td>
              <td className="px-4 py-3 font-mono text-xs text-gray-600">{disp.ip}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-600">{disp.usuario}</td>
              {showGroups && (
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-2">
                    {(disp.grupos || []).length > 0 ? (
                      (disp.grupos || []).map((grupo) => (
                        <span key={grupo.id} className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
                          {grupo.nome}
                        </span>
                      ))
                    ) : (
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
                        Sem grupo
                      </span>
                    )}
                  </div>
                </td>
              )}
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onTestarConexao(disp)}
                    disabled={testingId === disp.id}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-700 transition duration-200 hover:bg-emerald-50 disabled:opacity-50"
                  >
                    {testingId === disp.id ? 'Testando...' : 'Testar conexão'}
                  </button>
                  <button
                    onClick={() => onConfigurar(disp)}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-blue-600 transition duration-200 hover:bg-blue-50"
                  >
                    Configurar
                  </button>
                  <button
                    onClick={() => onExcluir(disp)}
                    title="Excluir facial"
                    className="rounded-lg p-1.5 text-slate-400 transition duration-200 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {dispositivos.length === 0 && (
            <tr>
              <td colSpan={showGroups ? '9' : '8'} className="px-6 py-10 text-center text-sm text-gray-400">
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const MarcaBadge = ({ marca }) => {
  const normalized = (marca || '').toLowerCase();
  const styles = normalized.includes('intelbras')
    ? 'bg-emerald-50 text-emerald-700'
    : normalized.includes('hikvision')
      ? 'bg-orange-50 text-orange-700'
      : 'bg-slate-100 text-slate-600';

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${styles}`}>
      {{ rtsp_facial: 'Facial RTSP + ESP32', rtsp_lpr: 'Leitor LPR' }[normalized] || marca || 'sem marca'}
    </span>
  );
};

export default DispositivoPage;
